/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import fetch from 'isomorphic-fetch';
import DOMPurify from 'dompurify';

const MAX_REDIRECTS = 10;

/*
 * Strip the HTML head and footer put on the page by the Developer portal.
 * Return just the content. Which is in a div with the class "apicMainContent".
 */
function stripPortalHeader(data) {
  if (!data) {
    return null;
  }

  const parser = new DOMParser();
  const el = parser.parseFromString(data, 'text/html');

  // find element with class apicMainContent, assume just 1
  const content = el.querySelector('.apicMainContent');
  if (content) {
    // Sanitize HTML content to prevent XSS attacks
    return DOMPurify.sanitize(content.innerHTML, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
      ALLOWED_ATTR: ['href', 'title', 'class', 'target', 'rel']
    });
  }

  return data;
}

/*
 * Get the content from the body tag, if there is one.
 */
function getBodyContent(data) {
  if (!data) {
    return null;
  }

  const parser = new DOMParser();
  const el = parser.parseFromString(data, 'text/html');
  if (el.body) {
    // Sanitize HTML content to prevent XSS attacks
    return DOMPurify.sanitize(el.body.innerHTML, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td'],
      ALLOWED_ATTR: ['href', 'title', 'class', 'target', 'rel']
    });
  }

  return data;
}

/*
 * Handle response. If error, call callback which will be reject.
 * If the response code is not 200 then call reject. If redirect then
 * call again with the redirect URL.
 * requestNumber is the number of requests to this point, in case of
 * redirects.
 */
async function invokeHelperResponseHandler(targetUrl, removePortalHeader, requestNumber, response, request, isError, callback, reject) {
  // Network error
  if (response instanceof Error) {
    console.error(response);
    let responseDataString;
    if (response.toString) {
      responseDataString = response.toString();
    } else {
      responseDataString = `${response}`;
    }

    return callback(responseDataString);
  }

  let responseData = await response.text();

  if (response.status <= 200) {
    // ok
    if (removePortalHeader) {
      // process response to strip header
      responseData = stripPortalHeader(responseData);
    } else {
      responseData = getBodyContent(responseData);
    }
    callback(responseData);
  } else if (response.status >= 300 && response.status <= 307) {
    // redirect in Location field
    let newTargetUrl;
    if (response.headers) {
      newTargetUrl = response.headers.get('Location');
    }

    if (newTargetUrl) {
      // invoke redirected url
      const newRequestNumber = requestNumber + 1;
      if (newRequestNumber < MAX_REDIRECTS) {
        this.invokeHelper(newTargetUrl, removePortalHeader, newRequestNumber, callback, reject);
      } else {
        // too many redirects
        return reject(`Too many redirects, count: ${newRequestNumber} last URL: ${newTargetUrl}`);
      }
    }

    return reject(`No target url in the Location header, HTTP redirect status code: ${response.status}`);
  }

  // HTTP error code
  responseData = getBodyContent(responseData);
  if (responseData === null || responseData === '') {
    responseData = `<h2>${response.status}</h2><h3>${response.statusText}</h3>`;
  }
  return reject(responseData);
}

async function invokeHelper(targetUrl, removePortalHeader, requestNumber, resolve, reject) {
  const headers = new Headers();
  headers.set('Accept', 'text/html');

  const req = {
    method: 'get',
    url: targetUrl,
    headers
  };

  try {
    const response = await fetch(targetUrl, req);
    invokeHelperResponseHandler(targetUrl, removePortalHeader, requestNumber, response, req, false, resolve, reject);
  } catch (err) {
    invokeHelperResponseHandler(targetUrl, removePortalHeader, requestNumber, err, req, true, reject, reject);
  }
}

/*
 * Retrieve the content of the url using a HTTP GET.
 * returns a Promise that resolves with the content.
 * If stripPortalHeader is true, process the response to remove the portal
 * header and footer and just return the content part of the page.
 */
export default function retrieveDocHtml(targetUrl, removePortalHeader) {
  return new Promise((resolve, reject) => {
    invokeHelper(targetUrl, removePortalHeader, 0, resolve, reject);
  });
}
export { retrieveDocHtml };
